// Ekran 07'nin sağ sütunu — besleme raporu.
// Blok sırası ve sınıf adları ref_files/btys-erp-07-besleme.html raporHTML()
// ile birebir: .inst → Proses Yükleri → .inst → 2'li bilanço → kalem tablosu →
// uyarılar + .pnote.
//
// Buradaki hiçbir sayı İSTEMCİDE hesaplanmıyor; §6.2'nin tamamı sunucudan
// geliyor (Btys.Domain/Calc/Besleme.cs). Yalnız yüzde payları ve rozet sınıfı
// gösterim düzeyinde türetiliyor.

import { tr } from '../../lib/trFormat'
import type { Bant, Gosterge, RaporDto } from './api'

const BANT_BADGE: Record<Bant, string> = {
  saglikli: 'badge b-navy',
  izle: 'badge b-amber',
  kritik: 'badge b-red',
  yok: 'badge b-gray',
}

/** .inst içindeki değerin rengi — amber/red sınıfı bant ile aynı kaynaktan. */
const INST_RENK: Record<Bant, string> = {
  saglikli: '',
  izle: ' amber',
  kritik: ' red',
  yok: '',
}

const KAYNAK_ETIKET: Record<string, [string, string]> = {
  analiz: ['Analiz', 'src-analiz'],
  tesis: ['Tesis', 'src-tesis'],
  lit: ['Literatür', 'src-lit'],
}

/** K13 kaynağının rozeti; kaynak bilinmiyorsa literatür sayılır. */
const kaynakRozeti = (kaynak: string | null): [string, string] =>
  KAYNAK_ETIKET[kaynak ?? ''] ?? KAYNAK_ETIKET.lit

/** Kojen kapasitesi — referansın 24.000 kWh/gün'ü (1.000 kWe × 24 sa). */
const KOJEN_KWH_GUN = 24000

function UyariIkonu({ kirmizi }: { kirmizi: boolean }) {
  return (
    <span className="ui" style={{ color: kirmizi ? 'var(--red)' : 'var(--amber-dot)' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" />
      </svg>
    </span>
  )
}

/** Bir gösterge satırı (.ptab). Birim değere yapışık yazılır. */
function GostergeSatiri({ g }: { g: Gosterge }) {
  const deger =
    g.deger === null
      ? '—'
      : g.birim === '%'
        ? `%${tr(g.deger, 1)}`
        : g.birim === 'gün'
          ? `${tr(g.deger, 0)} gün`
          : g.birim === 'ppm' || g.birim === 'mg/L'
            ? `${tr(g.deger, 0)} ${g.birim}`
            // Birimsiz tek satır C/N — özet şeridiyle aynı basamakta kalsın.
            : g.birim === null
              ? tr(g.deger, 1)
              : tr(g.deger, 2)

  return (
    <tr>
      <td>{g.ad}</td>
      <td className="num" style={{ fontWeight: 600 }}>{deger}</td>
      <td className="tur-t">{g.hedef}</td>
      <td>
        {g.bant === 'yok'
          ? <span className="tur-t">{g.etiket}</span>
          : <span className={BANT_BADGE[g.bant]}>{g.etiket}</span>}
      </td>
    </tr>
  )
}

type Props = {
  rapor: RaporDto
  hedefler: { id: string | null; ad: string; hacim: number }[]
  hedefId: string | null
  hedefDegis: (id: string | null) => void
  kilitli: boolean
}

export default function BeslemeRaporu({ rapor, hedefler, hedefId, hedefDegis, kilitli }: Props) {
  const kmBant = rapor.gostergeler.find((g) => g.ad.startsWith('Karışım kuru'))?.bant ?? 'yok'
  const cnBant = rapor.gostergeler.find((g) => g.ad.startsWith('C/N'))?.bant ?? 'yok'
  const netIsi =
    rapor.isi === null || rapor.isitmaIhtiyaciKwh === null
      ? null
      : Math.max(0, rapor.isi - rapor.isitmaIhtiyaciKwh)

  return (
    <div>
      <div className="inst">
        <div className="inst-t">Karışım Özeti — canlı</div>
        <div className="inst-3">
          <div className="inst-c">
            <div className="inst-v">{tr(rapor.toplamT, 1)}</div>
            <div className="inst-l">t/gün · {tr(rapor.hacim, 1)} m³</div>
          </div>
          <div className="inst-c">
            <div className={`inst-v${INST_RENK[kmBant]}`}>{tr(rapor.kmMix, 1)}</div>
            <div className="inst-l">% KM · ρ {tr(rapor.yogunlukMix, 0)} kg/m³</div>
          </div>
          <div className="inst-c">
            <div className="inst-v">{tr(rapor.okmToplam, 2)}</div>
            <div className="inst-l">t oKM · bozunma %{tr(rapor.bozunmaAgirlikli, 0)}</div>
          </div>
          <div className="inst-c">
            <div className={`inst-v${INST_RENK[cnBant]}`}>{tr(rapor.cnOrani, 1)}</div>
            <div className="inst-l">C/N oranı</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 8 }}>
        <div className="sect-t">
          Proses Yükleri — hedef çürütücü:
          <select
            className="sel"
            style={{ marginLeft: 6, height: 22, padding: '1px 5px', fontSize: 11 }}
            value={hedefId ?? ''}
            disabled={kilitli}
            onChange={(e) => hedefDegis(e.target.value === '' ? null : e.target.value)}
          >
            {hedefler.map((h) => (
              <option key={h.id ?? 'tumu'} value={h.id ?? ''}>
                {h.ad} · {tr(h.hacim, 0)} m³
              </option>
            ))}
          </select>{' '}
          <span className="cell-sub">(K10)</span>
        </div>
        <table className="ptab">
          <thead>
            <tr>
              <th>Gösterge</th>
              <th className="num">Değer</th>
              <th>Hedef / Sınır</th>
              <th>Durum</th>
            </tr>
          </thead>
          <tbody>
            {rapor.gostergeler.map((g) => <GostergeSatiri key={g.ad} g={g} />)}
          </tbody>
        </table>
      </div>

      <div className="inst" style={{ marginTop: 8 }}>
        <div className="inst-t">Beklenen Üretim — BMP öncelikli (K13)</div>
        <div className="inst-3">
          <div className="inst-c">
            <div className="inst-v">{tr(rapor.biyogaz, 0)}</div>
            <div className="inst-l">Nm³ biyogaz · CH₄ %{tr(rapor.ch4Ortalama, 1)}</div>
          </div>
          <div className="inst-c">
            <div className="inst-v">{tr(rapor.metan, 0)}</div>
            <div className="inst-l">Nm³ metan · {rapor.bmpliKalem} kalem BMP</div>
          </div>
          <div className="inst-c">
            <div className="inst-v">{tr(rapor.elektrik, 0)}</div>
            <div className="inst-l">kWh elektrik (η %40)</div>
          </div>
          <div className="inst-c">
            <div className="inst-v">{tr(rapor.isi, 0)}</div>
            <div className="inst-l">kWh ısı (η %43)</div>
          </div>
        </div>
        <div className="inst-note">
          Kojen {tr(KOJEN_KWH_GUN, 0)} kWh/gün kapasite → yük{' '}
          <b>%{tr(rapor.elektrik === null ? null : (rapor.elektrik / KOJEN_KWH_GUN) * 100, 0)}</b>{' '}
          · metan: ölçülen BMP varsa BMP, yoksa verim × CH₄%
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
        <div className="card">
          <div className="sect-t">Azot &amp; Kükürt Yük Bilançosu</div>
          <table className="ptab">
            <tbody>
              <tr>
                <td>Toplam azot girişi (TKN)</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.nYuku, 1)}</td>
                <td className="tur-t" style={{ width: 70 }}>kg N/gün</td>
              </tr>
              <tr>
                <td>— hazır amonyum (NH₄-N)</td>
                <td className="num">{tr(rapor.nh4Yuku, 1)}</td>
                <td className="tur-t">kg/gün</td>
              </tr>
              <tr>
                <td>Reaktör TAN tahmini</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.tan, 0)}</td>
                <td className="tur-t">mg/L</td>
              </tr>
              <tr>
                <td>
                  Serbest NH₃ payı
                  {rapor.curutucuPh !== null && ` (pH ${tr(rapor.curutucuPh, 1)} · 38 °C)`}
                </td>
                <td className="num">
                  {rapor.fan === null || rapor.tan === null || rapor.tan === 0
                    ? '—'
                    : `%${tr((rapor.fan / rapor.tan) * 100, 1)}`}
                </td>
                <td className="tur-t">FAN/TAN</td>
              </tr>
              <tr>
                <td>Kükürt girişi (S)</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.sYuku, 2)}</td>
                <td className="tur-t">kg S/gün</td>
              </tr>
              <tr>
                <td>H₂S kütlesi (dönüşüm 0,40)</td>
                <td className="num">{tr(rapor.h2sKg, 2)}</td>
                <td className="tur-t">kg/gün</td>
              </tr>
              <tr>
                <td>Aktif karbon ihtiyacı</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.aktifKarbonKg, 1)}</td>
                <td className="tur-t">kg AC/gün</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="sect-t">Kütle &amp; Enerji Bilançosu</div>
          <table className="ptab">
            <tbody>
              <tr>
                <td>Gaza dönüşen kütle (ρ 1,25)</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.gazKutle, 2)}</td>
                <td className="tur-t" style={{ width: 70 }}>t/gün</td>
              </tr>
              <tr>
                <td>Dijestat çıkışı</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.dijestat, 1)}</td>
                <td className="tur-t">t/gün</td>
              </tr>
              <tr>
                <td>Dijestat KM / oKM</td>
                <td className="num">%{tr(rapor.dijestatKm, 1)} / %{tr(rapor.dijestatOkm, 1)}</td>
                <td className="tur-t">tahmini</td>
              </tr>
              <tr>
                <td>Besleme ısıtma ihtiyacı (15→38°C)</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.isitmaIhtiyaciKwh, 0)}</td>
                <td className="tur-t">kWh/gün</td>
              </tr>
              <tr>
                <td>Isı öz-tüketim payı</td>
                <td className="num">
                  {rapor.isi === null || rapor.isi === 0 || rapor.isitmaIhtiyaciKwh === null
                    ? '—'
                    : `%${tr((rapor.isitmaIhtiyaciKwh / rapor.isi) * 100, 0)}`}
                </td>
                <td className="tur-t">üretilen ısıdan</td>
              </tr>
              <tr>
                <td>Net satılabilir ısı</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(netIsi, 0)}</td>
                <td className="tur-t">kWh/gün</td>
              </tr>
              <tr>
                <td>Dijestat gübre değeri (N·P·K)</td>
                <td className="num" style={{ fontWeight: 600 }}>{tr(rapor.gubreDegeriTl, 0)}</td>
                <td className="tur-t">₺/gün eşd.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: 8 }}>
        <div className="sect-t">Kalem Bazında Analiz — firma × ürün</div>
        <table className="ptab">
          <thead>
            <tr>
              <th>Firma × Ürün</th>
              <th className="num">t</th>
              <th className="num">KM %</th>
              <th className="num">oKM t</th>
              <th className="num">Biyogaz Nm³</th>
              <th className="num">Metan Nm³</th>
              <th>CH₄ kayn.</th>
              <th className="num">kWh</th>
              <th className="num">Pay</th>
            </tr>
          </thead>
          <tbody>
            {rapor.kalemler.map((k, i) => (
                <tr key={`${k.stokKod}-${k.cariUnvan}-${i}`}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {k.stokAd}{' '}
                      <span className={`src-b ${kaynakRozeti(k.kaynakTipi)[1]}`}>
                        {kaynakRozeti(k.kaynakTipi)[0]}
                      </span>
                    </div>
                    <div className="cell-sub">
                      {k.cariUnvan}{k.planMi && ' · girişsiz plan'}
                    </div>
                  </td>
                  <td className="num">{tr(k.miktarT, 1)}</td>
                  <td className="num">{tr(k.etkinKm, 2)}</td>
                  <td className="num">{tr(k.okm, 2)}</td>
                  <td className="num">{tr(k.biyogaz, 0)}</td>
                  <td className="num">{tr(k.metan, 0)}</td>
                  <td className="tur-t">
                    {k.metanKaynagi === 'bmp'
                      ? `● BMP ${tr(k.metanKaynakDegeri, 0)}`
                      : k.metanKaynagi === 'ch4'
                        ? `CH₄ %${tr(k.metanKaynakDegeri, 0)}`
                        : '—'}
                  </td>
                  <td className="num">{tr(k.elektrik, 0)}</td>
                  <td className="num">
                    {k.biyogaz === null || rapor.biyogaz === null || rapor.biyogaz === 0
                      ? '—'
                      : `${tr((k.biyogaz / rapor.biyogaz) * 100, 1)}%`}
                  </td>
                </tr>
            ))}
            <tr style={{ background: '#F8FAFD', fontWeight: 700 }}>
              <td>Toplam</td>
              <td className="num">{tr(rapor.toplamT, 1)}</td>
              <td className="num">{tr(rapor.kmMix, 2)}</td>
              <td className="num">{tr(rapor.okmToplam, 2)}</td>
              <td className="num">{tr(rapor.biyogaz, 0)}</td>
              <td className="num">{tr(rapor.metan, 0)}</td>
              <td />
              <td className="num">{tr(rapor.elektrik, 0)}</td>
              <td className="num">{rapor.kalemler.length ? '100%' : '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: 8 }}>
        <div className="sect-t">Uyarılar &amp; Kısıtlar</div>
        {rapor.uyarilar.length === 0 ? (
          <div className="uyari-li" style={{ color: 'var(--muted)' }}>
            Kısıt ihlali yok — menü hedef bantların içinde.
          </div>
        ) : (
          rapor.uyarilar.map((u, i) => (
            <div className="uyari-li" key={`${u.kuralRef}-${i}`}>
              <UyariIkonu kirmizi={u.bant === 'red'} />
              <span>{u.mesaj}</span>
            </div>
          ))
        )}
        {/* K34: model varsayımları dipnotu — sunucudan geliyor, hepsi yazılır. */}
        <div className="pnote">
          <b>Model varsayımları:</b>{' '}
          {rapor.varsayimlar.join(' · ')}
        </div>
      </div>
    </div>
  )
}

export { KAYNAK_ETIKET }
